import { useContext, useMemo } from 'react';
import FinancingType from '~/constants/FinancingType';
import AppliedFiltersContext from '~/contexts/AppliedFiltersContext';
import ConditionalTileContext from '~/contexts/ConditionalTileContext';
import DeviceContext from '~/contexts/DeviceContext';
import {
  useFinanceInfo,
  useFinanceType,
  useTradeInfo
} from '~/contexts/FinanceAndTradeContext';
import SearchContext from '~/contexts/SearchContext';
import type { ConditionalTile } from '~/contracts';
import getConditionalTiles from '~/services/conditionalTiles';

function useHasEligibleDownPaymentRange() {
  const financeInfo = useFinanceInfo();

  return financeInfo
    ? financeInfo.minDownPayment >= 0 && financeInfo.minDownPayment <= 1000
    : false;
}

export default function useConditionalTiles(): ConditionalTile[] {
  const { appliedFilters, pageNumber } = useContext(AppliedFiltersContext);
  const { filteredByMinDown } = useContext(ConditionalTileContext);
  const { browserName, deviceName, screenSize } = useContext(DeviceContext);
  const { showFinance } = useContext(SearchContext);
  const hasSoftPull = useFinanceType() === FinancingType.UserFinancing;

  const hasEligibleDownPaymentRange = useHasEligibleDownPaymentRange();
  const tradeInfo = useTradeInfo();

  const hasActiveTrade = Boolean(tradeInfo);
  const hasPositiveEquityIfActiveTrade =
    tradeInfo?.totalTradeInEquity == null || tradeInfo.totalTradeInEquity > 0;

  return useMemo(
    () =>
      getConditionalTiles({
        device: { browserName, deviceName, ...screenSize },
        filteredByMinDown,
        filters: appliedFilters,
        hasActiveTrade,
        hasEligibleDownPaymentRange,
        hasPositiveEquityIfActiveTrade,
        hasSoftPull,
        pageNumber,
        showFinance
      }),
    [
      appliedFilters,
      browserName,
      deviceName,
      filteredByMinDown,
      hasActiveTrade,
      hasEligibleDownPaymentRange,
      hasPositiveEquityIfActiveTrade,
      hasSoftPull,
      pageNumber,
      screenSize,
      showFinance
    ]
  );
}
